import React, {useMemo} from "react";
import {ColumnDef, Row} from "@tanstack/react-table";
import {nameof} from "ts-simple-nameof";
import {FilterOptions, InitialFilterModel} from "../../../core/components/reactTable/ReactBaseTable";
import { getCellTooltipId } from 'core/components/reactTable/ReactTableHelpers';
import useDashboardList from "./useDashboardList";
import {Localizer} from "../../../infrastructure/localization/localizer";
import ReactSyncTable from "../../../core/components/reactTable/ReactSyncTable";
import {useLocation} from "react-router-dom";
import {
    AttestationStatusEnum,
    DashboardListUkoModel,
    EvalueringStatusEnum,
    GetAttestationString,
    GetEvalueringString
} from "./models/DashboardListForloebUkoModel";
import {ManglerVejlederUkoCell} from "./manglerVejlederUkoCell";
import {GetStatusString, VejledersamtaleStatusEnum} from "../../vejledning/vejledersamtaler/VejledersamtaleStatusEnum";
import ProgressBar, {ProgressBarSegment} from "./dashboardProgressCell";
import {
    DashboardUddannelsesplanStatusEnum,
    GetUddannelsesplanStatusString
} from "../../vejledning/uddannelsesplan/DashboardUddannelsesplanStatusEnum";

export const DashboardListUko = () => {
    const {forloebListUko, isLoading} = useDashboardList();
    const location = useLocation();
    const initialFilter = location.state as InitialFilterModel[];

    function calculatePercentage<T>(items: T[], predicate: (item: T) => boolean): number {
        if (!items || items.length === 0)
            return 0;
        const matchingItems = items.filter(predicate).length;
        return (matchingItems / items.length) * 100;
    }

    function generateProgressBarTitle<T>(
        status: T,
        statuses: T[],
        getStatusString: (status: T) => string
    ): string {
        const count = statuses?.filter(s => s === status).length;
        return `${getStatusString(status)}: ${count}`;
    }

    const columns: ColumnDef<DashboardListUkoModel>[] = useMemo(() => [

        {
            accessorKey: nameof<DashboardListUkoModel>(x => x.uddannelsesstedNavn),
            header: Localizer.uddannelsessted(),
            cell: info => info.getValue(),
            id: nameof<DashboardListUkoModel>(x => x.uddannelsesstedNavn).toLowerCase(),
            meta: {
                filterVariant: 'text',
            },
        },
        {
            accessorKey: nameof<DashboardListUkoModel>(x => x.antalForloeb),
            header: Localizer.global_antal(),
            id: nameof<DashboardListUkoModel>(x => x.antalForloeb).toLowerCase(),
            cell: info => info.getValue(),
            aggregationFn: undefined,
            meta: {
                filterVariant: 'text',
            },
        },
        {
            accessorKey: nameof<DashboardListUkoModel>(x => x.antalForloebManglerVejleder),
            header: Localizer.vejleder(),
            id: nameof<DashboardListUkoModel>(x => x.antalForloebManglerVejleder).toLowerCase(),
            cell: info => <ManglerVejlederUkoCell antalVejledere={info.getValue() as number}/>,
            aggregationFn: undefined,
            meta: {
                filterVariant: 'range',
            },
        },
        {
            accessorKey: nameof<DashboardListUkoModel>(x => x.antalKlarmeldteKompetencer),
            header: Localizer.global_kompetencer(),
            id: nameof<DashboardListUkoModel>(x => x.antalKlarmeldteKompetencer).toLowerCase(),
            cell: info => `${info.getValue() as string} ${Localizer.global_klarmeldinger().toLowerCase()}`,
            aggregationFn: undefined,
            meta: {
                filterVariant: 'range',
            },
        },
        {
            accessorKey: nameof<DashboardListUkoModel>(x => x.evalueretStatus),
            header: Localizer.global_evaluering(),
            id: nameof<DashboardListUkoModel>(x => x.evalueretStatus).toLowerCase(),
            enableSorting: false,
            cell: info => <ProgressBar segments={[
                {
                    percentage: calculatePercentage(info.getValue() as EvalueringStatusEnum[], (s) => s === EvalueringStatusEnum.Indsendt),
                    className: 'progress-bar-green',
                    title: generateProgressBarTitle(EvalueringStatusEnum.Indsendt, info.getValue() as EvalueringStatusEnum[], GetEvalueringString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as EvalueringStatusEnum[], (s) => s === EvalueringStatusEnum.ManglerIndsendelse),
                    className: 'progress-bar-gray',
                    title: generateProgressBarTitle(EvalueringStatusEnum.ManglerIndsendelse, info.getValue() as EvalueringStatusEnum[], GetEvalueringString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as EvalueringStatusEnum[], (s) => s === EvalueringStatusEnum.Overskredet),
                    className: 'progress-bar-red',
                    title: generateProgressBarTitle(EvalueringStatusEnum.Overskredet, info.getValue() as EvalueringStatusEnum[], GetEvalueringString)
                },
            ] as ProgressBarSegment []} tooltipId={getCellTooltipId(info.row.index, info.cell.id)}/>,
            aggregationFn: undefined,
        },
        {
            accessorKey: nameof<DashboardListUkoModel>(x => x.attestationStatus),
            header: Localizer.forloebpage_Attestation(),
            id: nameof<DashboardListUkoModel>(x => x.attestationStatus).toLowerCase(),
            enableSorting: false,
            cell: info => <ProgressBar segments={[
                {
                    percentage: calculatePercentage(info.getValue() as AttestationStatusEnum[], (s) => s === AttestationStatusEnum.Godkendt),
                    className: 'progress-bar-green',
                    title: generateProgressBarTitle(AttestationStatusEnum.Godkendt, info.getValue() as AttestationStatusEnum[], GetAttestationString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as AttestationStatusEnum[], (s) => s === AttestationStatusEnum.IkkeKlarTilGodkendelse),
                    className: 'progress-bar-gray',
                    title: generateProgressBarTitle(AttestationStatusEnum.IkkeKlarTilGodkendelse, info.getValue() as AttestationStatusEnum[], GetAttestationString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as AttestationStatusEnum[], (s) => s === AttestationStatusEnum.ManglerGodkendelse),
                    className: 'progress-bar-red',
                    title: generateProgressBarTitle(AttestationStatusEnum.ManglerGodkendelse, info.getValue() as AttestationStatusEnum[], GetAttestationString)
                },
            ] as ProgressBarSegment []} tooltipId={getCellTooltipId(info.row.index, info.cell.id)}/>,
            aggregationFn: undefined,
        },
        {
            accessorKey: nameof<DashboardListUkoModel>(x => x.introVejledersamtalerStatus),
            header: Localizer.dashboard_foerste_introsamtale(),
            id: nameof<DashboardListUkoModel>(x => x.introVejledersamtalerStatus).toLowerCase(),
            enableSorting: false,
            cell: info => <ProgressBar segments={[
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.EndnuIkkePlanlagt),
                    className: 'progress-bar-gray',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.EndnuIkkePlanlagt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.Planlagt),
                    className: 'progress-bar-blue',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.Planlagt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.Afholdt),
                    className: 'progress-bar-green',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.Afholdt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.Klarmeldt),
                    className: 'progress-bar-yellow',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.Klarmeldt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.OverskredetFrist),
                    className: 'progress-bar-red',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.OverskredetFrist, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
            ] as ProgressBarSegment []} tooltipId={getCellTooltipId(info.row.index, info.cell.id)}/>,
            aggregationFn: undefined,
        },
        {
            accessorKey: nameof<DashboardListUkoModel>(x => x.justerendeVejledersamtalerStatus),
            header: Localizer.dashboard_foerste_justerendesamtale(),
            id: nameof<DashboardListUkoModel>(x => x.justerendeVejledersamtalerStatus).toLowerCase(),
            enableSorting: false,
            cell: info => <ProgressBar segments={[
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.EndnuIkkePlanlagt),
                    className: 'progress-bar-gray',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.EndnuIkkePlanlagt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.Planlagt),
                    className: 'progress-bar-blue',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.Planlagt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)},
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.Afholdt),
                    className: 'progress-bar-green',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.Afholdt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.Klarmeldt),
                    className: 'progress-bar-yellow',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.Klarmeldt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.OverskredetFrist),
                    className: 'progress-bar-red',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.OverskredetFrist, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
            ] as ProgressBarSegment []} tooltipId={getCellTooltipId(info.row.index, info.cell.id)}/>,
            aggregationFn: undefined,
        },
        {
            accessorKey: nameof<DashboardListUkoModel>(x => x.afsluttendeVejledersamtalerStatus),
            header: Localizer.dashboard_foerste_afsluttendesamtale(),
            id: nameof<DashboardListUkoModel>(x => x.afsluttendeVejledersamtalerStatus).toLowerCase(),
            enableSorting: false,
            cell: info => <ProgressBar segments={[
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.EndnuIkkePlanlagt),
                    className: 'progress-bar-gray',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.EndnuIkkePlanlagt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.Planlagt),
                    className: 'progress-bar-blue',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.Planlagt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.Afholdt),
                    className: 'progress-bar-green',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.Afholdt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.Klarmeldt),
                    className: 'progress-bar-yellow',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.Klarmeldt, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as VejledersamtaleStatusEnum[], (s) => s === VejledersamtaleStatusEnum.OverskredetFrist),
                    className: 'progress-bar-red',
                    title: generateProgressBarTitle(VejledersamtaleStatusEnum.OverskredetFrist, info.getValue() as VejledersamtaleStatusEnum[], GetStatusString)
                },
            ] as ProgressBarSegment []} tooltipId={getCellTooltipId(info.row.index, info.cell.id)}/>,
            aggregationFn: undefined,
        },
        {
            accessorKey: nameof<DashboardListUkoModel>(x => x.foersteUddannelsesplanStatus),
            header: Localizer.dashboard_foersteUddannelsesplan(),
            id: nameof<DashboardListUkoModel>(x => x.foersteUddannelsesplanStatus).toLowerCase(),
            enableSorting: false,
            cell: info => <ProgressBar segments={[
                {
                    percentage: calculatePercentage(info.getValue() as DashboardUddannelsesplanStatusEnum[], (s) => s === DashboardUddannelsesplanStatusEnum.EndnuIkkeOprettet),
                    className: 'progress-bar-gray',
                    title: generateProgressBarTitle(DashboardUddannelsesplanStatusEnum.EndnuIkkeOprettet, info.getValue() as DashboardUddannelsesplanStatusEnum[], GetUddannelsesplanStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as DashboardUddannelsesplanStatusEnum[], (s) => s === DashboardUddannelsesplanStatusEnum.Oprettet),
                    className: 'progress-bar-blue',
                    title: generateProgressBarTitle(DashboardUddannelsesplanStatusEnum.Oprettet, info.getValue() as DashboardUddannelsesplanStatusEnum[], GetUddannelsesplanStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as DashboardUddannelsesplanStatusEnum[], (s) => s === DashboardUddannelsesplanStatusEnum.Godkendt),
                    className: 'progress-bar-green',
                    title: generateProgressBarTitle(DashboardUddannelsesplanStatusEnum.Godkendt, info.getValue() as DashboardUddannelsesplanStatusEnum[], GetUddannelsesplanStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as DashboardUddannelsesplanStatusEnum[], (s) => s === DashboardUddannelsesplanStatusEnum.Klarmeldt),
                    className: 'progress-bar-yellow',
                    title: generateProgressBarTitle(DashboardUddannelsesplanStatusEnum.Klarmeldt, info.getValue() as DashboardUddannelsesplanStatusEnum[], GetUddannelsesplanStatusString)
                },
                {
                    percentage: calculatePercentage(info.getValue() as DashboardUddannelsesplanStatusEnum[], (s) => s === DashboardUddannelsesplanStatusEnum.OverskredetFrist),
                    className: 'progress-bar-red',
                    title: generateProgressBarTitle(DashboardUddannelsesplanStatusEnum.OverskredetFrist, info.getValue() as DashboardUddannelsesplanStatusEnum[], GetUddannelsesplanStatusString)
                },
            ] as ProgressBarSegment []} tooltipId={getCellTooltipId(info.row.index, info.cell.id)}/>,
            aggregationFn: undefined,
        },
    ], []);

    const filterOptions: FilterOptions = {
        pageSizeOptions: [10, 25, 50, 100],
        hidePageSizeFilter: false,
        hideColumnSettingsButton: false,
        initialSortColumn: nameof<DashboardListUkoModel>(x => x.uddannelsesstedNavn).toLowerCase(),
        initialFilter: initialFilter,
    };

    const rowColorCallback = (row: Row<DashboardListUkoModel>): string => {
        return "";
    };

    return (
        <ReactSyncTable
            tableId="dashboardListUko"
            data={forloebListUko}
            columns={columns}
            isLoading={isLoading}
            filterOptions={filterOptions}
            rowColorCallback={rowColorCallback}
        />);
}
